import { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LenisContext } from "@/App";

const Nav = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lenisRef = useContext(LenisContext);

  const goCollection = () => {
    setOpen(false);
    if (pathname === "/") {
      lenisRef.current?.scrollTo("#collection", { offset: -60 });
    } else {
      navigate("/#collection");
    }
  };

  const linkCls = (active) =>
    `text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:text-gilt ${
      active ? "text-gilt" : "text-sand"
    }`;

  return (
    <header
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-moss-700/60 bg-moss-950/90 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3">
          <span className="block h-11 w-11 overflow-hidden rounded-full border border-moss-700">
            <img
              src="/logo.webp"
              alt="Faeloria Collective logo"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          </span>
          <span className="leading-tight">
            <span className="block font-script text-2xl text-ivory">Faeloria</span>
            <span className="block text-[9px] uppercase tracking-[0.4em] text-sand">Collective</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <Link data-testid="nav-link-about" to="/about" className={linkCls(pathname === "/about")}>
            Our Story
          </Link>
          <button data-testid="nav-link-collection" onClick={goCollection} className={linkCls(false)}>
            Creations
          </button>
          <Link data-testid="nav-link-info" to="/info" className={linkCls(pathname === "/info")}>
            Herbal Index
          </Link>
          <Link data-testid="nav-link-rituals" to="/about#rituals" className={linkCls(false)}>
            Rituals
          </Link>
          <Link data-testid="nav-link-safety" to="/info#safety" className={linkCls(false)}>
            Safety
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="text-ivory md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-moss-700/60 bg-moss-950 md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              <Link data-testid="nav-mobile-about" to="/about" onClick={() => setOpen(false)} className="text-left font-serif text-2xl italic text-ivory">
                Our Story
              </Link>
              <button data-testid="nav-mobile-collection" onClick={goCollection} className="text-left font-serif text-2xl italic text-ivory">
                Creations
              </button>
              <Link data-testid="nav-mobile-info" to="/info" onClick={() => setOpen(false)} className="font-serif text-2xl italic text-ivory">
                Herbal Index
              </Link>
              <Link data-testid="nav-mobile-rituals" to="/about#rituals" onClick={() => setOpen(false)} className="font-serif text-2xl italic text-ivory">
                Rituals
              </Link>
              <Link data-testid="nav-mobile-safety" to="/info#safety" onClick={() => setOpen(false)} className="font-serif text-2xl italic text-ivory">
                Safety
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
